import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'
import { BilingualMarkdownEditor } from '../../components/MarkdownField'

/**
 * Super-admin Terms of Use versions (reseller program).
 *
 * Lists published versions (the current one is what everyone must accept),
 * lets an admin publish a new version (which forces re-acceptance for every
 * user), and shows the per-user acceptance audit log for any version.
 */
// Terms "Type" (backed by terms_versions.Audience). Reseller = the reseller
// program user accepts in the portal; Customer = the reseller's end customer
// accepts at booking checkout.
const TYPES = [
  { value: 'reseller', label: 'Reseller', who: 'every reseller user', when: 'on their next portal visit' },
  { value: 'customer', label: 'Customer', who: "every reseller's customer", when: 'at their next booking checkout' },
]

export default function TermsVersions() {
  const [audience, setAudience] = useState('reseller')
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [acceptancesFor, setAcceptancesFor] = useState(null)

  const typeMeta = TYPES.find(t => t.value === audience) || TYPES[0]

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/admin/resellers/terms-versions', { params: { audience } })
      .then(r => setVersions(r.data?.versions || []))
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  // Reload whenever the selected Type changes.
  useEffect(reload, [audience])

  return (
    <AdminShell title="Terms of Use">
      <p style={{ color: '#64748b', maxWidth: 760, marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>
        The <strong>current</strong> version is the one {typeMeta.who} must accept. Publishing a new version
        immediately forces everyone to re-accept {typeMeta.when}. Each acceptance is logged with the user,
        timestamp, IP, and user-agent.
      </p>

      {/* Type selector — each Type has its own independent set of versions. */}
      <div style={tabRow}>
        {TYPES.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={() => { setShowForm(false); setAcceptancesFor(null); setAudience(t.value) }}
            style={audience === t.value ? tabActive : tab}
          >
            {t.label} terms
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <button type="button" onClick={() => setShowForm(s => !s)} style={primaryBtn}>
          {showForm ? 'Cancel' : `+ Publish new ${typeMeta.label} version`}
        </button>
      </div>

      {showForm && <PublishForm audience={audience} typeLabel={typeMeta.label} onPublished={() => { setShowForm(false); reload() }} />}

      {error && <div style={errorBox}>{error}</div>}

      {loading ? <div style={empty}>Loading…</div> : (
        <div style={tableWrap}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Version</th>
                <th style={th}>Title</th>
                <th style={th}>Effective</th>
                <th style={th}>Current</th>
                <th style={th}>Content</th>
                <th style={th}>Acceptances</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.id} style={tr}>
                  <td style={td}><strong>{v.version}</strong></td>
                  <td style={td}>{v.title || '—'}</td>
                  <td style={td}>{v.effectiveDate ? new Date(v.effectiveDate).toLocaleString() : '—'}</td>
                  <td style={td}>{v.isCurrent ? <span style={badge}>Current</span> : ''}</td>
                  <td style={td}>{v.contentMd ? 'Custom' : <span style={{ color: '#94a3b8' }}>Bundled copy</span>}</td>
                  <td style={td}>{v.acceptCount}</td>
                  <td style={td}>
                    <button type="button" onClick={() => setAcceptancesFor(v)} style={secondaryBtn}>View acceptances</button>
                  </td>
                </tr>
              ))}
              {versions.length === 0 && <tr><td style={td} colSpan={7}>No versions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {acceptancesFor && (
        <AcceptancesModal version={acceptancesFor} onClose={() => setAcceptancesFor(null)} />
      )}
    </AdminShell>
  )
}

function PublishForm({ audience = 'reseller', typeLabel = 'Reseller', onPublished }) {
  const [version, setVersion] = useState('')
  const [titleEn, setTitleEn] = useState('Terms of Use')
  const [titleFr, setTitleFr] = useState('Conditions d’utilisation')
  const [summaryEn, setSummaryEn] = useState('')
  const [summaryFr, setSummaryFr] = useState('')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [contentEn, setContentEn] = useState('')
  const [contentFr, setContentFr] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    if (!version.trim()) { setErr('A version label is required.'); return }
    setSaving(true); setErr(null)
    try {
      await tournamentApi.post('/admin/resellers/terms-versions', {
        audience,
        version: version.trim(),
        title: titleEn.trim() || undefined,
        titleFr: titleFr.trim() || undefined,
        summary: summaryEn.trim() || undefined,
        summaryFr: summaryFr.trim() || undefined,
        contentMd: contentEn.trim() || undefined,
        contentMdFr: contentFr.trim() || undefined,
        effectiveDate: effectiveDate || undefined,
      })
      onPublished?.()
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit} style={formCard}>
      <div style={{ marginBottom: 14, fontSize: 13, color: '#0f172a' }}>
        Type: <span style={{ ...badge, background: '#e0e7ff', color: '#3730a3' }}>{typeLabel}</span>
        <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>(switch the Type tab above to publish for the other audience)</span>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Version label (e.g. 2026-07)" style={{ flex: '1 1 200px' }}>
          <input value={version} onChange={e => setVersion(e.target.value)} required style={inp} placeholder="2026-07" />
        </Field>
        <Field label="Effective date (optional)" style={{ flex: '1 1 200px' }}>
          <input type="datetime-local" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} style={inp} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Title (English)" style={{ flex: '1 1 200px' }}>
          <input value={titleEn} onChange={e => setTitleEn(e.target.value)} style={inp} />
        </Field>
        <Field label="Title (French)" style={{ flex: '1 1 200px' }}>
          <input value={titleFr} onChange={e => setTitleFr(e.target.value)} style={inp} />
        </Field>
      </div>
      <Field label="Content (EN/FR tabs — leave blank to use the app's bundled Terms copy)">
        <BilingualMarkdownEditor
          valueEn={contentEn} valueFr={contentFr}
          onChangeEn={setContentEn} onChangeFr={setContentFr}
        />
      </Field>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Summary of changes (English)" style={{ flex: '1 1 200px' }}>
          <input value={summaryEn} onChange={e => setSummaryEn(e.target.value)} style={inp} placeholder="What changed" />
        </Field>
        <Field label="Summary of changes (French)" style={{ flex: '1 1 200px' }}>
          <input value={summaryFr} onChange={e => setSummaryFr(e.target.value)} style={inp} />
        </Field>
      </div>
      {err && <div style={errorBox}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" disabled={saving} style={primaryBtn}>{saving ? 'Publishing…' : `Publish ${typeLabel} version and make current`}</button>
      </div>
      <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
        Publishing makes this the current <strong>{typeLabel}</strong> version and forces re-acceptance from that audience.
      </p>
    </form>
  )
}

function AcceptancesModal({ version, onClose }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    tournamentApi.get(`/admin/resellers/terms-versions/${version.id}/acceptances`)
      .then(r => setRows(r.data?.acceptances || []))
      .catch(e => setError(e?.response?.data?.message || e.message))
  }, [version.id])

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Acceptances — {version.version}</h3>
          <button type="button" onClick={onClose} style={secondaryBtn}>Close</button>
        </div>
        {error && <div style={errorBox}>{error}</div>}
        {!rows ? <div style={empty}>Loading…</div> : rows.length === 0 ? <div style={empty}>No acceptances yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Account</th>
                  <th style={th}>Via</th>
                  <th style={th}>When</th>
                  <th style={th}>IP</th>
                  <th style={th}>User agent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={tr}>
                    <td style={td}>{r.email}{r.name ? <div style={{ fontSize: 12, color: '#64748b' }}>{r.name}</div> : null}</td>
                    <td style={td}>{r.accountName || r.accountId || '—'}</td>
                    <td style={td}>{r.via || '—'}</td>
                    <td style={td}>{r.acceptedAt ? new Date(r.acceptedAt).toLocaleString() : '—'}</td>
                    <td style={td}>{r.ip || '—'}</td>
                    <td style={{ ...td, maxWidth: 240, fontSize: 11, color: '#64748b', wordBreak: 'break-all' }}>{r.userAgent || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  )
}

const tabRow = { display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }
const tab = { padding: '10px 16px', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: -1 }
const tabActive = { ...tab, color: '#0f172a', borderBottom: '2px solid #0f172a' }
const primaryBtn = { padding: '10px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const formCard = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 24 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, margin: '8px 0', fontSize: 13 }
const empty = { padding: 30, textAlign: 'center', color: '#64748b' }
const tableWrap = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflowX: 'auto' }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '12px 16px', verticalAlign: 'top' }
const badge = { padding: '3px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontSize: 11, fontWeight: 600 }
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 900, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }
